import { useEffect, useState } from 'react';
import { IPublicClientApplication } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { datadogLogs } from '@datadog/browser-logs';
import { getAuthConfig } from './Api/Auth/AuthConfigApi';
import { createMsalInstance } from './EntraConfig';
import { App } from './App';
import { EntraAuthProvider } from './Context/EntraAuthProvider';
import { OktaAppContainer } from './OktaAppContainer';

type SignInRoute =
  { provider: 'okta' } | { provider: 'entra'; msalInstance: IPublicClientApplication; apiScope: string };

const resolveSignInRoute = async (): Promise<SignInRoute> => {
  const { data, error } = await getAuthConfig();
  if (!data) {
    datadogLogs.logger.warn('Could not read the sign-in provider; using Okta', { error: error?.message });
    return { provider: 'okta' };
  }
  if (data.provider !== 'entra') return { provider: 'okta' };
  if (!data.entra) {
    datadogLogs.logger.warn('The API chose Entra sign-in but sent no Entra settings; using Okta');
    return { provider: 'okta' };
  }

  try {
    return { provider: 'entra', msalInstance: await createMsalInstance(data.entra), apiScope: data.entra.apiScope };
  } catch (failure) {
    datadogLogs.logger.error('MSAL failed to start; using Okta', { error: failure });
    return { provider: 'okta' };
  }
};

// The provider comes from the API rather than the build so it can be switched without redeploying the web app.
export const IdpContainer = () => {
  const [route, setRoute] = useState<SignInRoute | null>(null);

  useEffect(() => {
    const load = async () => setRoute(await resolveSignInRoute());
    void load();
  }, []);

  if (!route) return null;
  if (route.provider === 'entra') {
    return (
      <MsalProvider instance={route.msalInstance}>
        <EntraAuthProvider apiScope={route.apiScope}>
          <App />
        </EntraAuthProvider>
      </MsalProvider>
    );
  }
  return <OktaAppContainer />;
};
